<template>
    <vx-modal
        :visible="!!model"
        @update:visible="close"
        :padding="false"
        size="md"
    >
        <template v-slot:header>
            <div class="px-6 pt-6">
                Are you sure you want to delete this {{ modelTitle }}?
            </div>
        </template>
        <template v-slot:subheader>
            <div class="px-6">
                This {{ modelTitle }} and its association to contacts will be deleted. There is no undo.
            </div>
        </template>
        <div class="p-6">
            <div v-if="loadingUsage">
                <vx-icon-loading class="w-9 h-9" :spin="true"></vx-icon-loading>
            </div>
            <vx-alert v-else-if="failedToLoadUsage" color="danger" :closeable="false">
                Failed to load data.
                <a href="#" @click.prevent="loadUsage()">Retry.</a>
            </vx-alert>
            <vx-alert v-else :color="usedAnywhere ? 'info' : 'primary'" :closeable="false">
                <div v-for="line in lines" :key="line">{{ line }}</div>
            </vx-alert>
        </div>
        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button @click="close"
                    size="lg"
                    color="muted">
                    Cancel
                </vx-button>
                <vx-button @click="remove"
                    data-test="del-btn"
                    :disabled="loadingUsage"
                    type="button"
                    size="lg"
                    color="danger"
                    class="lg:grow">
                    Delete
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup>
import { VxAlert, VxButton, VxIconLoading, VxModal } from '@voxie/frontend-components';
import { computed, inject, ref, watch } from 'vue';
import { pluralized } from '~/components/filters';
import { automationRulesClient, customAttributesClient, segmentsClient, tagsClient, workflowsClient } from '~/services';
import { useToasted } from '~/composables/useToasted';

const FEATURES = inject('FEATURES');
const teamId = Spark.state.currentTeam.id;
const toasted = useToasted();

const model = defineModel({
    type: String,
});
const emit = defineEmits(['deleted']);

const props = defineProps({
    type: {
        type: String,
        validator: type => ['tag', 'custom_attribute'].includes(type),
    }
});

const loadingUsage = ref(false);
const failedToLoadUsage = ref(false);
const usage = ref(null);

const fieldName = computed(() => {
    switch (props.type) {
        case 'tag':
            return 'filter[tag_name]';
        case 'custom_attribute':
            return 'filter[custom_attribute_key]';
    }

    return undefined;
});

const modelTitle = computed(() => {
    switch (props.type) {
        case 'tag':
            return 'tag';
        case 'custom_attribute':
            return 'custom attribute';
    }

    return undefined;
});

const kinds = computed(() =>
    FEATURES.workflow_builder ? 'segments, automations or workflows' : 'segments or automations'
);

const lines = computed(() => {
    if (usage.value === null) {
        return [];
    }

    const used = Object.entries(usage.value).filter(([, { count }]) => count > 0);

    if (!used.length) {
        return [`The ${modelTitle.value} is not used in any ${kinds.value}.`];
    }

    return used.map(([kind, { count, names }]) => {
        const label = `The ${modelTitle.value} is used in ${pluralized(kind, count, true)}: ${names.join(', ')}`;

        return label + (names.length < count ? ', etc.' : '.');
    });
});

const usedAnywhere = computed(() => usage.value !== null && Object.values(usage.value).some(({ count }) => count > 0));

watch(() => model.value, (value) => {
    if (value) {
        loadUsage();
    }
});

const remove = async () => {
    let request;

    if (props.type === 'tag') {
        request = tagsClient.tagDelete(teamId, model.value);
    } else if (props.type === 'custom_attribute') {
        request = customAttributesClient.delete(teamId, model.value);
    } else {
        return;
    }

    request.then(() => {
        emit('deleted', model.value);
        close();
    }).catch((error) => {
        console.error(error);
        toasted.global.platform_error();
    });
};

const close = () => {
    model.value = null;
};

const loadUsage = async () => {
    if (!model.value || !fieldName.value) {
        return;
    }

    loadingUsage.value = true;
    failedToLoadUsage.value = false;

    const params = { [fieldName.value]: model.value };

    try {
        const [segments, segmentsCount, automations, automationsCount, workflows] = await Promise.all([
            segmentsClient.getSegments({ teamId }, { ...params, per_page: 5 }),
            segmentsClient.getSegmentsCount({ teamId }, params),
            automationRulesClient.search(teamId, { ...params, per_page: 5 }),
            automationRulesClient.automationRulesCount(teamId, params),
            FEATURES.workflow_builder ? workflowsClient.getWorkflowUsage({ teamId }, params) : null,
        ]);

        const loaded = {
            segment: { count: segmentsCount.data.count, names: segments.data.data.map((segment) => segment.name) },
            automation: { count: automationsCount.data.count, names: automations.data.data.map((rule) => rule.name) },
        };

        if (workflows) {
            loaded.workflow = {
                count: workflows.data.data.length,
                names: workflows.data.data.slice(0, 5).map((workflow) => workflow.name),
            };
        }

        usage.value = loaded;
    } catch {
        failedToLoadUsage.value = true;
    } finally {
        loadingUsage.value = false;
    }
};
</script>
